import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, TextField, Button, Paper, Alert } from '@mui/material';
import { getAis, createCollection } from '../services/api';

interface Collection {
  _id: string;
  version: string;
  createdAt: string;
}

interface AI {
  id: string;
  title: string;
  domain: string;
  description: string;
  collections: Collection[];
}

const CreateVersion = () => {
  const { aiId } = useParams<{ aiId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract query parameters
  const queryParams = new URLSearchParams(location.search);
  const fromCollectionId = queryParams.get('fromCollectionId');
  const initialVersionName = queryParams.get('versionName') || '';
  
  const [version, setVersion] = useState(initialVersionName);
  const [ai, setAi] = useState<AI | null>(null);
  const [sourceCollection, setSourceCollection] = useState<Collection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!aiId) return;

    // Fetch AI details to show the AI name and find the source collection
    getAis()
      .then((res) => {
        const currentAi = res.data.find((ai: AI) => ai.id === aiId);
        if (currentAi) {
          setAi(currentAi);
          
          // Find source collection details if a fromCollectionId was provided
          if (fromCollectionId) {
            const source = currentAi.collections.find((col: Collection) => col._id === fromCollectionId);
            if (source) {
              setSourceCollection(source);
            } else {
              setError('Source collection not found');
            }
          }
        } else {
          setError('AI not found');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading AI:', err);
        setError('Failed to load AI details');
        setLoading(false);
      });
  }, [aiId, fromCollectionId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!version.trim()) {
      setError('Please enter a version name');
      return;
    }

    setLoading(true);

    // Create the new version using the specified source collection
    createCollection(aiId!, { version, fromCollectionId: fromCollectionId || undefined })
      .then(() => {
        setSuccess(true);
        setError('');
        // Clear the form
        setVersion('');
        // Redirect after 2 seconds
        setTimeout(() => {
          navigate(`/my-ais`);
        }, 2000);
      })
      .catch((err) => {
        console.error('Error creating collection:', err);
        setError(err.response?.data?.message || 'Failed to create collection');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Create New Version
      </Typography>

      {ai && (
        <Typography variant="h6" color="text.secondary" gutterBottom>
          AI: {ai.title}
        </Typography>
      )}
      
      {sourceCollection && (
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Cloning from version: {sourceCollection.version}
        </Typography>
      )}

      {sourceCollection && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
          Note: For complex AIs with large databases, the cloning process may take a few minutes to complete.
        </Typography>
      )}

      <Paper sx={{ p: 3, mt: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>Version created successfully! Redirecting...</Alert>}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Version Name"
            variant="outlined"
            fullWidth
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            required
            disabled={loading || success}
            margin="normal"
            helperText="Enter a name for this version (e.g. '1.0', 'beta', etc.)"
          />

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button 
              type="button"
              variant="outlined"
              disabled={loading || success}
              onClick={() => navigate('/my-ais')}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              variant="contained"
              disabled={loading || success}
              sx={{ ml: 'auto' }}
            >
              Create Version
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default CreateVersion;